import React, { useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Zoom from '@mui/material/Zoom';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { red, green, blue, amber } from '@mui/material/colors';
import { BackIcon } from '../../../components/AppHeader';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AdjustIcon from '@mui/icons-material/Adjust';
import WarningIcon from '@mui/icons-material/Warning';
import AccessibleIcon from '@mui/icons-material/Accessible';
import { useTranslation } from 'react-i18next';
import { camelCase } from 'lodash-es';
import Snackbar from '../../../components/Snackbar';
import { getLatestScoresPerStage } from './ShooterSquadSelect';
import { summarizeScore, pad } from '../../../utils/scoring';
import { updateScore, deleteScore as apiDeleteScore } from '../../../api';

const CELL = {
  display: 'block', textAlign: 'center', lineHeight: '40px',
  width: 40, height: 40, margin: 2, border: '1px solid rgba(0,0,0,0.5)',
};
const CELL_BLANK = { ...CELL, border: '1px solid white' };
const HEADER = { ...CELL_BLANK, height: 20, lineHeight: '20px', fontSize: '0.75rem' };
const SUMMARY_CELL = {
  display: 'table-cell', textAlign: 'center', lineHeight: '40px',
  width: 43, height: 40, borderRight: '2px solid black', borderTop: '2px solid black', borderBottom: '2px solid black',
};
const CAPTION = { fontSize: '0.75rem', fontWeight: 400, textAlign: 'center' };

export default function ScoreReview() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { stageIdx, squadId, shooterId } = useParams();
  const { matchId, match, stages, shooters, scores, refreshScores, setPendingScore, canEditScores } = useOutletContext();
  const stageIdxNum = parseInt(stageIdx, 10);
  const basePath = `/${matchId}/manage/scores`;

  const shooter = shooters.find((s) => s.id === shooterId);
  const stage = stages[stageIdxNum - 1];

  const latestScores = getLatestScoresPerStage(scores, stages.length);
  const stageScores = latestScores[stageIdxNum - 1] || [];
  const score = stageScores.find((s) => s.shooterId === shooterId);

  const scoreModePath = `${basePath}/stage/${stageIdx}/squad/${squadId}/score-mode`;
  const [searchParams] = useSearchParams();
  const fromSearch = searchParams.get('fromSearch') || squadId === '_nosquad';
  const splitParam = searchParams.get('split');
  const backPath = fromSearch ? `${basePath}/stage/${stageIdx}` : `${scoreModePath}${splitParam ? `?split=${splitParam}` : ''}`;
  const scoreMode = match?.scoreMode || 'comstock';
  const displayMode = match?.scoreFormDisplayMode || 'normal';

  const [lockLoading, setLockLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [transfering, setTransfering] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const editable = score?.editable;

  // Build signature URLs (the endpoint returns PNG binary)
  const sigUrl = (sigValue) => {
    if (!sigValue) return null;
    if (String(sigValue).startsWith('data:')) return sigValue;
    const baseUrl = import.meta.env.VITE_API_URL || '';
    const auth = sessionStorage.getItem('accessToken') || '';
    return `${baseUrl}/api/matches/${matchId}/scores/signatures/${sigValue}?auth=${encodeURIComponent(auth)}`;
  };
  const roSigUrl = sigUrl(score?.roSignature);
  const shooterSigUrl = sigUrl(score?.signature);

  const summary = useMemo(() => {
    if (!score || score.dq || !stage || !shooter) return null;
    return summarizeScore({
      score: {
        scorePerTarget: score.scorePerTarget || [],
        poppers: score.poppers || 0,
        tenPointsPoppers: score.tenPointsPoppers || 0,
        'no-shoots': score['no-shoots'] || 0,
        penaltiesByType: score.penaltiesByType || {},
        specialPenalty: score.specialPenalty || 0,
        time: score.time || 0,
        bonusField: score.bonusField || 0,
      },
      powerFactor: shooter['power-factor'],
      stage,
      mode: scoreMode,
    });
  }, [score, stage, scoreMode, shooter]);

  // All shooters as transfer/swap targets (auto-detect based on existing score)
  const transferTargets = useMemo(() => {
    if (!score) return [];
    const scoreByShooter = {};
    stageScores.forEach((s) => { scoreByShooter[s.shooterId] = s; });
    return shooters
      .filter((s) => !s.cancelledParticipation && s.id !== shooterId)
      .map((s) => {
        const existingScore = scoreByShooter[s.id];
        return {
          ...s,
          scoreId: existingScore?.id || null,
          hasScore: !!existingScore,
          _label: `#${s.publicId} ${s.name}`,
        };
      });
  }, [shooters, stageScores, score, shooterId]);

  const handleToggleLock = async () => {
    setLockLoading(true);
    try {
      await updateScore(matchId, score.id, { editable: !editable });
      await refreshScores();
    } catch {
      setSnack({ open: true, message: t('scoring.sendError'), severity: 'error' });
    } finally {
      setLockLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await updateScore(matchId, score.id, { editable: false });
      await apiDeleteScore(matchId, score.id);
      await refreshScores();
      setDeleteConfirm(false);
      navigate(backPath);
    } catch {
      setSnack({ open: true, message: t('scoring.sendError'), severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleTransferOrSwap = async (targetShooter) => {
    setTransfering(true);
    try {
      if (targetShooter.hasScore) {
        // Swap: unlock the other score, swap both, lock both
        await updateScore(matchId, targetShooter.scoreId, { editable: true });
        await Promise.all([
          updateScore(matchId, score.id, { shooterId: targetShooter.id, editable: false }),
          updateScore(matchId, targetShooter.scoreId, { shooterId: shooterId, editable: false }),
        ]);
      } else {
        // Transfer: just move the score
        await updateScore(matchId, score.id, { shooterId: targetShooter.id, editable: false });
      }
      await refreshScores();
      setTransferDialog(false);
      navigate(backPath);
    } catch {
      // Rollback on swap failure
      if (targetShooter.hasScore) {
        try {
          await Promise.all([
            updateScore(matchId, score.id, { shooterId: shooterId }),
            updateScore(matchId, targetShooter.scoreId, { shooterId: targetShooter.id }),
          ]);
        } catch { /* ignore rollback errors */ }
      }
      setSnack({ open: true, message: t('scoring.sendError'), severity: 'error' });
    } finally {
      setTransfering(false);
    }
  };

  const handleEdit = async () => {
    // Load the existing score into pending and navigate to scoring
    const scoreData = {
      scorePerTarget: score.scorePerTarget || [],
      poppers: score.poppers || 0,
      tenPointsPoppers: score.tenPointsPoppers || 0,
      'no-shoots': score['no-shoots'] || 0,
      penaltiesByType: score.penaltiesByType || {},
      specialPenalty: score.specialPenalty || 0,
      bonusField: score.bonusField || 0,
      time: score.time || 0,
      powerFactor: shooter?.['power-factor'] || 'minor',
    };
    setPendingScore({
      stageIdx: stageIdxNum,
      shooterId,
      ...scoreData,
    });
    // Save to localStorage with the same key ScoreEntry uses
    const storageKey = `__scoring_entry_${matchId}_${stageIdx}`;
    localStorage.setItem(storageKey, JSON.stringify({
      scoreData,
      isDq: false,
      dqReason: '',
      dqOccuredOn: '',
    }));
    navigate(`${scoreModePath}/scoring/${shooterId}${splitParam ? `?split=${splitParam}` : ''}`, { state: { fromSignature: true } });
  };

  if (!shooter || !stage) {
    return <Typography color="error">Shooter or stage not found</Typography>;
  }

  if (!score) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)}>
          {t('common.back')}
        </Button>
        <Typography sx={{ mt: 2 }}>{t('results.noScores')}</Typography>
        <Button variant="contained" sx={{ mt: 2 }}
          onClick={() => navigate(`${scoreModePath}/scoring/${shooterId}${splitParam ? `?split=${splitParam}` : ''}`, { replace: true })}>
          {t('scoring.score')}
        </Button>
      </Box>
    );
  }

  const targets = stage.targets || {};
  const numPaper = (targets.paper || 0) + (targets.minipaper || 0);
  const numMetal = (targets.poppers || 0) + (targets.minipoppers || 0) + (targets.plates || 0) + (targets['pcc-frangible'] || 0);
  const numTenPoints = (targets['pcc-ten-points-poppers'] || 0) + (targets['pcc-ten-points-frangible'] || 0);
  const isDq = !!score.dq;
  const poppers = score.poppers || 0;
  const tenPointsPoppers = score.tenPointsPoppers || 0;
  const scorePerTarget = score.scorePerTarget || [];
  const noShoots = score['no-shoots'] || 0;
  const penaltiesByType = score.penaltiesByType || {};
  const specialPenalty = score.specialPenalty || 0;
  const bonusField = score.bonusField || 0;
  const time = score.time || 0;
  const procTotal = Object.values(penaltiesByType).reduce((s, v) => s + v, 0);
  const metalMissesValue = scoreMode === 'steel-no-miss' ? '' : (!time ? '' : (numMetal - poppers) || '');
  const cLabel = scoreMode === 'comstock-abd' ? 'B' : 'C';

  const shooterInfo = [
    shooter.division ? t(`divisions.${camelCase(shooter.division)}`, shooter.division) : null,
    shooter.category ? t(`categories.${camelCase(shooter.category)}`, shooter.category) : null,
    shooter['power-factor'] ? t(`registration.${camelCase(shooter['power-factor'])}`, shooter['power-factor']) : null,
  ].filter(Boolean).join(' | ');

  const scoreDate = score.timestamp ? new Date(score.timestamp) : null;
  const formTime = scoreDate
    ? `${scoreDate.toLocaleDateString()} ${scoreDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : '';

  return (
    <Box sx={{ position: 'relative', pb: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)}>
          {t('common.back')}
        </Button>
        {/* Lock/Unlock button — only for users with score-editing permission */}
        {canEditScores && (
          <Button
            variant="contained"
            color={editable ? 'warning' : 'secondary'}
            disabled={lockLoading}
            onClick={handleToggleLock}
            sx={{ minWidth: 56, minHeight: 44 }}
          >
            {lockLoading
              ? <CircularProgress size={22} color="inherit" />
              : editable
                ? <LockOpenIcon sx={{ fontSize: 32 }} />
                : <LockIcon sx={{ fontSize: 28 }} />
            }
          </Button>
        )}
      </Box>

      {displayMode === 'normal' ? (
        /* Full score form display */
        <div style={{ background: 'white', boxShadow: '0px 0px 2px rgba(0,0,0,0.3)', padding: 12, margin: 'auto', marginBottom: 24, position: 'relative', direction: 'ltr', maxWidth: 500 }}>
          {/* DQ overlay */}
          {isDq && (
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, pointerEvents: 'none' }}>
              <div style={{ transform: 'rotate(-50deg)', fontSize: 64, textAlign: 'center' }}>
                <div style={{ fontSize: 24 }}>{shooter.publicId} / {shooter.name}</div>
                <div>DQ</div>
                <div>{score.dq}</div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', opacity: isDq ? 0.5 : 1 }}>
            {/* Left: Score table */}
            <div>
              {/* Column headers */}
              <div style={{ display: 'flex' }}>
                <div style={CELL_BLANK} />
                <div style={HEADER}>A</div>
                <div style={HEADER}>{cLabel}</div>
                <div style={HEADER}>D</div>
                <div style={HEADER}>M</div>
                <div style={{ ...HEADER, fontStyle: 'italic' }}>(M)</div>
              </div>

              {/* Metal targets row */}
              {numMetal > 0 && (
                <div style={{ display: 'flex' }}>
                  <div style={CELL_BLANK} />
                  <div style={CELL}>{poppers || ''}</div>
                  <div style={{ ...CELL_BLANK, width: 82, height: 36, margin: 4, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {t('results.poppers')}
                  </div>
                  <div style={CELL}>{metalMissesValue}</div>
                  {scoreMode === 'steel-no-miss' && (
                    <div style={CELL}>{!time ? '' : (numMetal - poppers) || ''}</div>
                  )}
                </div>
              )}

              {/* Ten points poppers row */}
              {numTenPoints > 0 && (
                <div style={{ display: 'flex' }}>
                  <div style={CELL_BLANK} />
                  <div style={CELL}>{tenPointsPoppers || ''}</div>
                  <div style={{ ...CELL_BLANK, width: 82, height: 36, margin: 4, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {t('results.tenPointsPoppers')}
                  </div>
                  <div style={CELL}>{scoreMode === 'steel-no-miss' ? '' : (!time ? '' : (numTenPoints - tenPointsPoppers) || '')}</div>
                  {scoreMode === 'steel-no-miss' && (
                    <div style={CELL}>{!time ? '' : (numTenPoints - tenPointsPoppers) || ''}</div>
                  )}
                </div>
              )}

              {/* Per-target rows */}
              {Array.from({ length: numPaper }).map((_, i) => {
                const s = scorePerTarget[i] || '';
                const a = (s.match(/A/g) || []).length;
                const c = (s.match(/C/g) || []).length;
                const d = (s.match(/D/g) || []).length;
                const m = s.split('').filter((ch) => ch === 'M').length;
                const bm = s.split('').filter((ch) => ch === 'm').length;
                return (
                  <div key={i} style={{ display: 'flex' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, margin: 2 }}>
                      T{i + 1}
                    </div>
                    <div style={CELL}>{a || ''}</div>
                    <div style={CELL}>{c || ''}</div>
                    <div style={CELL}>{d || ''}</div>
                    <div style={CELL}>{m || ''}</div>
                    <div style={CELL}>{bm || ''}</div>
                  </div>
                );
              })}
            </div>

            {/* Right: Match & shooter info */}
            <div style={{ marginLeft: 30 }}>
              <div style={{ fontSize: 13, fontWeight: 300, marginTop: 6 }}>{formTime}</div>
              <div style={{ ...CAPTION, textAlign: 'left', marginTop: 12 }}><u>{t('scoring.matchInfo')}</u></div>
              <div style={{ fontSize: 13, fontWeight: 900 }}>{match?.title || ''}</div>
              <div style={{ ...CAPTION, textAlign: 'left', marginTop: 12 }}>
                <u>{t('results.stageN', { n: stageIdx })}</u>
              </div>
              <div style={{ fontSize: 13, fontWeight: 300 }}>
                {t('scoring.ro')}:<b>{score.ro || ''}</b>
              </div>
              <div style={{ ...CAPTION, textAlign: 'left', marginTop: 12 }}><u>{t('scoring.shooterInfo')}</u></div>
              <div style={{ fontSize: 13, fontWeight: 300 }}>
                {shooter.publicId} / <b>{shooter.name}</b>
              </div>
              <div style={{ fontSize: 13, fontWeight: 300 }}>{shooterInfo}</div>
            </div>
          </div>

          {/* Summary section */}
          <div style={{ opacity: isDq ? 0.5 : 1 }}>
            <div style={{ margin: '6px 45px', width: 229, borderBottom: '2px solid black' }} />

            {/* Totals row */}
            <div style={{ marginLeft: 45 }}>
              <div style={{ ...SUMMARY_CELL, borderLeft: '2px solid black' }}>{summary?.alphas || ''}</div>
              <div style={SUMMARY_CELL}>{summary?.charlies || ''}</div>
              <div style={{ ...SUMMARY_CELL, width: 45 }}>{summary?.deltas || ''}</div>
              <div style={SUMMARY_CELL}>{summary?.misses || ''}</div>
              <div style={SUMMARY_CELL}>{summary?.bonusMisses || ''}</div>
              <div style={{ ...SUMMARY_CELL, borderTop: 'none', borderBottom: 'none' }} />
              <div style={SUMMARY_CELL}>{noShoots || ''}</div>
              <div style={SUMMARY_CELL}>{procTotal || ''}</div>
            </div>

            {/* Column labels */}
            <div style={{ display: 'flex' }}>
              <div style={CELL_BLANK} />
              <div style={HEADER}>A</div>
              <div style={HEADER}>{cLabel}</div>
              <div style={HEADER}>D</div>
              <div style={HEADER}>M</div>
              <div style={HEADER}>(M)</div>
              <div style={{ ...HEADER, width: 39 }} />
              <div style={HEADER}>PT</div>
              <div style={HEADER}>Proc</div>
            </div>

            {/* Penalty breakdown */}
            {procTotal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, marginLeft: 45 }}>
                <div style={{ fontSize: '0.8em', fontWeight: 800 }}>{t('results.penalties')}:&nbsp;</div>
                <div style={{ fontSize: '0.8em' }}>
                  {Object.keys(penaltiesByType).filter((r) => !!penaltiesByType[r]).map((r) => `${r} (${penaltiesByType[r]})`).join(' | ')}
                </div>
              </div>
            )}

            {/* Bonus field */}
            {stage.bonusField && (
              <div style={{ marginLeft: 45, marginTop: 24 }}>
                <div style={{ display: 'block', textAlign: 'center', lineHeight: '40px', width: 88, height: 40, border: '2px solid black' }}>
                  {bonusField}
                </div>
                <div style={{ ...CAPTION, width: 88 }}>{stage.bonusField}</div>
              </div>
            )}

            {/* Bottom summary: rounds | time | [special penalty] | total | factor */}
            <div style={{ marginLeft: 45, display: 'flex', marginTop: 24 }}>
              <SummaryBottomCell
                value={isDq ? '' : `${(summary?.alphas || 0) + (summary?.charlies || 0) + (summary?.deltas || 0) + (summary?.misses || 0) + (summary?.bonusMisses || 0)} / ${stage.rounds}`}
                color={!isDq && summary && ((summary.alphas + summary.charlies + summary.deltas + summary.misses + summary.bonusMisses) !== stage.rounds) ? '#f50057' : '#000'}
                first
              />
              <SummaryBottomCell value={isDq ? '' : (summary?.timeStr || pad(time, 2))} />
              {specialPenalty > 0 && (
                <SummaryBottomCell value={`-${summary?.specialPenaltyValue || 0}`} />
              )}
              <SummaryBottomCell value={isDq ? '' : (summary?.total ?? '')} />
              <SummaryBottomCell value={isDq ? '' : (summary?.factorStr || '')} />
            </div>
            <div style={{ marginLeft: 45, display: 'flex' }}>
              <SummaryBottomLabel
                label={t('scoring.totalRoundsLabel')}
                color={!isDq && summary && ((summary.alphas + summary.charlies + summary.deltas + summary.misses + summary.bonusMisses) !== stage.rounds) ? '#f50057' : '#000'}
              />
              <SummaryBottomLabel label={t('results.time')} />
              {specialPenalty > 0 && (
                <SummaryBottomLabel label={t('scoring.specialPenalty', { specialPenalty })} />
              )}
              <SummaryBottomLabel label={t('scoring.totalPoints')} />
              <SummaryBottomLabel label={t('results.factor')} />
            </div>
          </div>
        </div>
      ) : (
        /* Condensed (corona) display */
        <CondensedScoreView
          shooter={shooter}
          stageIdx={stageIdx}
          stage={stage}
          score={score}
          summary={summary}
          isDq={isDq}
          scoreMode={scoreMode}
          numTenPoints={numTenPoints}
          t={t}
          isRtl={i18n.dir() === 'rtl'}
        />
      )}

      {/* Signatures */}
      {(roSigUrl || shooterSigUrl) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 3 }}>
          {roSigUrl && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">{t('scoring.roSignature')}</Typography>
              <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 0.5, bgcolor: '#fafafa' }}>
                <img src={roSigUrl} alt="RO Signature" style={{ width: '100%', height: 100, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
              </Box>
            </Box>
          )}
          {shooterSigUrl && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">{t('scoring.signature')}</Typography>
              <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 0.5, bgcolor: '#fafafa' }}>
                <img src={shooterSigUrl} alt="Shooter Signature" style={{ width: '100%', height: 100, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Action FABs - visible when score is unlocked (editable) */}
      <Box sx={{ position: 'fixed', bottom: 24, right: 24, display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Zoom in={!!editable} timeout={200} unmountOnExit style={{ transitionDelay: '400ms' }}>
          <Fab color="error" onClick={() => setDeleteConfirm(true)}>
            <DeleteIcon />
          </Fab>
        </Zoom>
        <Zoom in={!!editable} timeout={200} unmountOnExit style={{ transitionDelay: '300ms' }}>
          <Fab color="primary" onClick={() => setTransferDialog(true)}>
            <SwapHorizIcon />
          </Fab>
        </Zoom>
        <Zoom in={!!editable} timeout={200} unmountOnExit style={{ transitionDelay: '200ms' }}>
          <Fab color="primary" onClick={handleEdit}>
            <EditIcon />
          </Fab>
        </Zoom>
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>{t('common.delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('scoring.areSureDeleteScore')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>{t('common.no')}</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : t('common.yes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer/Swap dialog */}
      <Dialog open={transferDialog} onClose={() => setTransferDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('scoring.transfer')}</DialogTitle>
        <DialogContent>
          {transfering && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}
          {!transfering && (
            <Autocomplete
              sx={{ mt: 1 }}
              options={transferTargets}
              getOptionLabel={(s) => s._label}

              onChange={(_, val) => val && handleTransferOrSwap(val)}
              renderInput={(params) => <TextField {...params} autoFocus placeholder={t('scoring.pickShooter')} />}
              value={null}
              blurOnSelect
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog(false)}>{t('common.back')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} message={snack.message} severity={snack.severity}
        onClose={() => setSnack({ ...snack, open: false })} />
    </Box>
  );
}

function SummaryBottomCell({ value, color, first }) {
  return (
    <div style={{
      display: 'block', textAlign: 'center', lineHeight: '40px',
      width: 88, height: 40, color: color || '#000',
      borderRight: '2px solid black', borderTop: '2px solid black', borderBottom: '2px solid black',
      ...(first ? { borderLeft: '2px solid black' } : {}),
    }}>
      {value}
    </div>
  );
}

function SummaryBottomLabel({ label, color }) {
  return (
    <div style={{ ...CAPTION, width: 88, color: color || '#000' }}>
      {label}
    </div>
  );
}

function CoronaRow({ keyName, icon, value, background, mandatory, grid, color, isRtl, variant }) {
  if (!value && !mandatory) return null;
  return (
    <Grid
      key={keyName || icon}
      size={grid || 12}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: background,
          padding: '6px',
          boxShadow: background !== 'transparent' ? 'rgba(0, 0, 0, 0.2) 1px 1px 6px' : '',
        }}
      >
        <Typography variant={variant || 'h5'} style={{ flex: 1, color: color || 'white' }}>
          {value || 0}
        </Typography>
        {!!icon && (
          <div
            style={{
              [isRtl ? 'borderRight' : 'borderLeft']: '1px solid white',
              width: '50px',
              textAlign: 'center',
              [isRtl ? 'paddingRight' : 'paddingLeft']: '6px',
              color: color || 'white',
              fontSize: '24px',
            }}
          >
            {icon}
          </div>
        )}
      </div>
    </Grid>
  );
}

export function CondensedScoreView({ shooter, stageIdx, stage, score, summary, isDq, scoreMode, numTenPoints, t, isRtl }) {
  if (isDq) {
    return (
      <Box sx={{ p: 2, border: '2px solid', borderColor: 'error.main', borderRadius: 1, mb: 2 }}>
        <Typography variant="h5">
          {((shooter.publicId || '').split('|').pop())} / {shooter.name} — {t('results.stageN', { n: stageIdx })}
        </Typography>
        <Typography variant="h4" color="error" sx={{ mt: 1 }}>DQ</Typography>
        <Typography variant="body1">{t(`dq.${score.dq}`, score.dq)}</Typography>
      </Box>
    );
  }

  const alphas = summary?.alphas || 0;
  const charlies = summary?.charlies || 0;
  const deltas = summary?.deltas || 0;
  const misses = summary?.misses || 0;
  const bonusMisses = summary?.bonusMisses || 0;
  const noShoots = score['no-shoots'] || 0;
  const penaltiesByType = score.penaltiesByType || {};
  const procTotal = Object.values(penaltiesByType).reduce((s, v) => s + v, 0);
  const specialPenalty = summary?.specialPenaltyValue || score.specialPenalty || 0;
  const tenPointsPoppers = score.tenPointsPoppers || 0;
  const timeStr = summary?.timeStr || String(score.time || 0);
  const total = summary?.total ?? 0;
  const factorStr = summary?.factorStr || '';
  const numCount = alphas + charlies + deltas + misses + bonusMisses;
  const cLabel = scoreMode === 'comstock-abd' ? 'B' : 'C';

  return (
    <div style={{ margin: 'auto', marginBottom: '24px' }}>
      <Grid container spacing={1}>
        {/* Header: shooter name + stage */}
        <CoronaRow
          keyName="Shooter"
          value={`${(shooter.publicId || '').split('|').pop()} / ${shooter.name}`}
          background="transparent"
          color="black"
          mandatory
          grid={6}
          variant="h6"
          isRtl={isRtl}
        />
        <CoronaRow
          keyName="Stage"
          value={<div style={{ textAlign: 'end' }}>{t('results.stageN', { n: stageIdx })}</div>}
          background="transparent"
          color="black"
          mandatory
          grid={6}
          variant="h6"
          isRtl={isRtl}
        />

        {/* Red row: penalties */}
        <CoronaRow keyName="M" icon="M" value={misses} background={red[500]} isRtl={isRtl} />
        <CoronaRow keyName="PT" icon="PT" value={noShoots} background={red[400]} isRtl={isRtl} />
        <CoronaRow keyName="PBT" icon={<WarningIcon />} value={procTotal} background={red[300]} isRtl={isRtl} />
        <CoronaRow keyName="SPV" icon={<AccessibleIcon />} value={specialPenalty} background={red[200]} isRtl={isRtl} />

        {/* Time card (left half) + Hit cards (right half) */}
        <Grid size={6}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: blue[500],
              height: '100%', boxSizing: 'border-box',
              padding: '6px',
              boxShadow: 'rgba(0, 0, 0, 0.2) 1px 1px 6px',
            }}
          >
            <AccessTimeIcon sx={{ color: 'white', fontSize: 48, pb: '6px' }} />
            <Typography variant="h4" style={{ color: 'white' }}>
              {timeStr}
            </Typography>
          </div>
        </Grid>
        <Grid size={6}>
          <Grid container spacing={1}>
            {numTenPoints > 0 && (
              <CoronaRow keyName="A+" icon="A+" value={tenPointsPoppers} background={green[500]} mandatory isRtl={isRtl} />
            )}
            <CoronaRow keyName="A" icon="A" value={alphas - tenPointsPoppers} background={green[500]} mandatory isRtl={isRtl} />
            <CoronaRow keyName="C" icon={cLabel} value={charlies} background={green[400]} mandatory isRtl={isRtl} />
            <CoronaRow keyName="D" icon="D" value={deltas} background={deltas === 0 ? green[300] : amber[300]} mandatory isRtl={isRtl} />
            <CoronaRow keyName="BM" icon="(M)" value={bonusMisses} background={amber[200]} isRtl={isRtl} />
          </Grid>
        </Grid>

        {/* Bottom row: count + factor */}
        <CoronaRow
          keyName="Count"
          icon={<AdjustIcon />}
          value={`${numCount} / ${stage.rounds}`}
          background={numCount === stage.rounds ? '#666' : red[500]}
          mandatory
          grid={6}
          isRtl={isRtl}
        />
        <CoronaRow
          keyName="F"
          icon={isRtl ? '👉' : '👈'}
          value={`${total}/${timeStr} = ${factorStr}`}
          background="#666"
          mandatory
          grid={6}
          isRtl={isRtl}
        />
      </Grid>
    </div>
  );
}
